import {FC, useCallback} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import cn from 'classnames/bind'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {FormProvider, useForm} from 'react-hook-form'
import * as yup from 'yup'

import FormFooter from 'components/blocks/FormFooter'
import FormHeader from 'components/blocks/FormHeader'
import Button from 'components/elements/Button'
import FieldInput from 'components/elements/fields/FieldInput'
import {posthogTrack, klaviyoTrack} from 'src/utils'
import {useAuthStore, useDraftStore, useRootStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import validations from 'src/utils/validations'

import s from './LoginForm.module.scss'

const cx = cn.bind(s)

const schema = yup
	.object({
		email: yup.string().email('please enter a valid email').required(),
		password: validations.getPasswordValidator(),
	})
	.required()

interface ILoginFormProps {
	className?: string
}

const LoginForm: FC<ILoginFormProps> = ({className}) => {
	const router = useRouter()
	const authStore = useAuthStore()
	const rootStore = useRootStore()
	const draftStore = useDraftStore()
	const [signInFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof authStore.signIn>(
		authStore.signIn,
	)

	const methods = useForm<yup.TypeOf<typeof schema>>({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
			password: '',
		},
	})

	const onSubmit = useCallback(
		async (data: yup.TypeOf<typeof schema>) => {
			posthogTrack('Login button clicked', {source: 'Sign in form'})

			const {draftId, type, template, theme} = draftStore

			const response = await signInFlow({
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				email: data.email!,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				password: data.password!,
				draftId,
				type,
			})({
				onSuccess: () => {
					posthogTrack('User successfully logged in', {email: data.email})
					klaviyoTrack('Logged in', {email: data.email})
				},
			})

			if (response?.data.ok) {
				if (response.data.data.draftId) {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					draftStore.setDraft(response.data.data.draftId, type!, template, theme)
					if (response.data.data.hasActiveSubscription) {
						return router.push('/download')
					} else {
						return router.push('/pricing')
					}
				}
				return router.push(rootStore.previousRoute || '/')
			}
		},
		[draftStore, rootStore.previousRoute, router, signInFlow],
	)

	return (
		<div className={cx('wrapper', className)}>
			<FormProvider {...methods}>
				<form className={s.form} data-testid="login-form">
					<FormHeader
						className={cx('header')}
						title="sign in"
						message={errorMessage || methods.formState.errors}
					/>

					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="email"
						type="email"
						label="Email address"
						placeholder="Write email address"
					/>
					<FieldInput
						wrapperClassName={cx('input-spaces')}
						name="password"
						type="password"
						label="Password"
						placeholder="Password"
					/>

					<div className={cx('forgot', 'fs-xs', 'ta-right')}>
						<Link
							href="/auth/reset-password"
							className={cx('forgot__link')}
							aria-label="To reset password page">
							Forgot Password?
						</Link>
					</div>

					<Button
						className={cx('btn')}
						disabled={methods.formState.isSubmitted && !methods.formState.isValid}
						size="big"
						isLoading={isLoading}
						onClick={methods.handleSubmit(onSubmit)}
						data-testid="login-btn">
						Log in
					</Button>
					<FormFooter />
				</form>
			</FormProvider>
		</div>
	)
}

LoginForm.displayName = 'LoginForm'

export default LoginForm
